// Estilos do shell global (cabeçalho/AppBar/Toolbar/fundo da área
// principal) da modernização do InfoChat (Entrega M3).
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend.
// Recebe o `theme` do MUI (já com `theme.infoChat` produzido pela
// Entrega M2) e devolve só objetos de estilo simples (CSS-in-JS), para
// serem mesclados nas classes já existentes de `layout/index.js`. Não
// aplica nada sozinha — quem decide onde/como usar é o próprio
// `layout/index.js`.
//
// Usa exclusivamente os tokens de `theme.infoChat` para cor. O cabeçalho
// acompanha a superfície principal clara/escura e usa a cor institucional
// apenas como acento, mantendo a página visualmente contínua.

import { createInfoChatDesignTokens } from "../theme/infoChatDesignTokens";

// Correção pós-revisão (M3): o cabeçalho branco/ardósia da Opção 2 deixava
// ilegíveis vários controles filhos (SuperConnectionsHeaderAlert,
// NotificationsVolume, NotificationsPopOver, AnnouncementsPopover,
// ChatPopover, seletor de idioma) que ainda têm `color: "white"`/`#fff`
// embutido e não podem ser tocados nesta entrega (escopo travado nos 3
// arquivos do shell). Por isso o cabeçalho MANTÉM temporariamente o fundo
// institucional dinâmico com texto/ícones brancos — o mesmo contraste que
// já existia antes desta modernização. O cabeçalho branco propriamente
// dito (com todos os controles filhos revisados) fica para uma entrega
// futura dedicada, que poderá então trocar este valor por
// `tokens.surfaces.base`/`tokens.text.primary`.
const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.spacing
  );

// Nunca deixa o shell quebrar caso `theme.infoChat` esteja ausente ou
// incompleto (ex.: um tema simulado em teste, ou uma renderização
// intermediária antes do tema real estar pronto). Em vez de duplicar
// valores literais aqui, reaproveita a MESMA função pura da Entrega M2
// — o fallback nunca pode divergir dos tokens reais.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }

  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

/**
 * Estilos do shell global modernizado, derivados só de `theme.infoChat`
 * (com fallback seguro). Não é aplicada a nenhuma tela/lista/conversa —
 * só ao AppBar/Toolbar/fundo principal do layout logado.
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 * @returns {{
 *   appBar: object,
 *   toolbar: object,
 *   headerActions: object,
 *   headerIconButton: object,
 *   contentSurface: object
 * }}
 */
export const createInfoChatGlobalShellStyles = theme => {
  const tokens = resolveInfoChatTokens(theme);

  return {
    // Sombra bem discreta do AppBar — a única parte da "borda"/elevação
    // mantida nesta correção. A borda inferior na cor institucional foi
    // removida: contra o fundo institucional (mesma cor), ela ficaria
    // sempre invisível — fazia sentido só no desenho de cabeçalho branco,
    // que fica para a entrega dedicada (ver comentário no topo do arquivo).
    appBar: {
	  boxShadow: tokens.shadows.sm,
	  borderBottom: `1px solid ${tokens.mode === "dark" ? tokens.brand.primaryColor : tokens.borders.subtle}`
    },

    // Fundo institucional dinâmico (mantido temporariamente) com
    // texto/ícones brancos — preserva o contraste que já existia antes
    // desta entrega, já que os controles filhos do cabeçalho ainda
    // dependem de texto/ícone branco fixo e não podem ser tocados aqui.
    toolbar: {
	  backgroundColor: tokens.surfaces.base,
	  color: tokens.text.primary
    },

    // Reservado para um futuro contêiner dedicado aos ícones de ação do
    // cabeçalho (não há um wrapper único para eles hoje — não introduzido
    // nesta entrega para não alterar a estrutura do JSX). Documentado aqui
    // para já existir na API do helper, pronto para uso quando esse
    // agrupamento for criado.
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: tokens.spacing.scale.xs
    },

    // Cor dos ícones/botões de ação do cabeçalho — branco, igual ao texto
    // do Toolbar acima (mesmo motivo: fundo institucional mantido
    // temporariamente). Não é consumida por nenhum className em
    // layout/index.js nesta correção (nenhum ícone precisou trocar de cor
    // desta vez), mas continua exposta aqui para manter a API do helper
    // completa e pronta para a entrega do cabeçalho branco.
    headerIconButton: {
	  color: tokens.text.secondary,
      transition: `color ${tokens.transitions.duration.fast}ms ${tokens.transitions.easing.standard}`
    },

    // Fundo da área principal (atrás do conteúdo das telas) — canvas
    // azul-cinza claro no modo claro, azul-ardósia profundo (nunca preto
    // puro) no escuro.
    contentSurface: {
      backgroundColor: tokens.surfaces.canvas
    }
  };
};

export default createInfoChatGlobalShellStyles;
